import React, { useCallback, useEffect, useMemo, useState } from "react";
import { addWeeks, format, getISOWeek, subWeeks } from "date-fns";
import { es } from "date-fns/locale";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button, Card } from "../components/legacy-ui.js";
import { AppModalFooter, AppModalShell, AppPageHeader, AppTable } from "../components/app-ui.js";
import { companyPageTitle } from "../utils/company.js";
import { formatCurrency, formatDate } from "../utils/formatters.js";
import { LoadingState } from "../components/motion.js";
import {
  createQueseraExpense,
  deleteQueseraExpense,
  getBcvRate,
  getExpenseCategories,
  getHistoricalRates,
  getQueseraExpenses,
  updateQueseraExpense,
} from "../utils/api.js";

const todayKey = () => format(new Date(), "yyyy-MM-dd");

const emptyForm = (category = "Quesera") => ({
  category,
  description: "",
  amount: "",
  amountUsd: "",
  currency: "BS",
  useBcv: true,
  expenseDate: todayKey(),
  notes: "",
});

const formatWeekDay = (value) => {
  if (!value) return "—";
  const key = String(value).slice(0, 10);
  if (key.length < 10) return "—";
  return format(new Date(`${key}T12:00:00`), "d MMM", { locale: es });
};

const formatRate = (rate) => {
  if (!rate && rate !== 0) return "";
  return Number(rate).toLocaleString("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const ExpensesModule = () => {
  const [weekDate, setWeekDate] = useState(new Date());
  const [formOpen, setFormOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ count: 0, amountInUSD: 0, amountInBS: 0 });
  const [previousTotals, setPreviousTotals] = useState({ count: 0, amountInUSD: 0, amountInBS: 0 });
  const [difference, setDifference] = useState({ count: 0, amountInUSD: 0, amountInBS: 0 });
  const [byCategory, setByCategory] = useState([]);
  const [startOfWeek, setStartOfWeek] = useState("");
  const [endOfWeek, setEndOfWeek] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState("");
  const [currentBcvRate, setCurrentBcvRate] = useState("");
  const [historicalRates, setHistoricalRates] = useState([]);
  const [rateDate, setRateDate] = useState("");
  const [categories, setCategories] = useState([]);

  const categoryLabel = (id) =>
    categories.find((item) => item.slug === id)?.name || id;

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [rateRes, catsRes, histRes] = await Promise.all([
          getBcvRate(),
          getExpenseCategories(),
          getHistoricalRates(),
        ]);
        setCurrentBcvRate(rateRes.data?.data?.rate || "");
        const unique = (histRes.data?.usd || []).reduce((acc, current) => {
          if (
            !acc.find(
              (item) =>
                item.last_update === current.last_update && item.price === current.price
            )
          ) {
            acc.push(current);
          }
          return acc;
        }, []);
        const sorted = unique.sort((a, b) => new Date(b.last_update) - new Date(a.last_update));
        setHistoricalRates(sorted);
        if (sorted.length > 0) {
          setRateDate(sorted[0].last_update.slice(0, 10));
        }
        const loaded = catsRes.data.data || [];
        setCategories(loaded);
        setForm((prev) => {
          const exists = loaded.some(
            (item) => item.slug === prev.category || item.name === prev.category
          );
          if (exists) return prev;
          return { ...prev, category: loaded[0]?.name || "Quesera" };
        });
      } catch (error) {
        console.error("Error loading expense lookups", error);
      }
    };
    loadLookups();
  }, []);

  const fetchWeek = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getQueseraExpenses({
        date: format(weekDate, "yyyy-MM-dd"),
        category: categoryFilter || undefined,
      });
      setRows(res.data.data || []);
      setTotals(res.data.totals || { count: 0, amountInUSD: 0, amountInBS: 0 });
      setPreviousTotals(res.data.previousTotals || { count: 0, amountInUSD: 0, amountInBS: 0 });
      setDifference(res.data.difference || { count: 0, amountInUSD: 0, amountInBS: 0 });
      setByCategory(res.data.byCategory || []);
      setStartOfWeek(res.data.startOfWeek || "");
      setEndOfWeek(res.data.endOfWeek || "");
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudieron cargar los gastos", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }, [weekDate, categoryFilter]);

  useEffect(() => {
    fetchWeek();
  }, [fetchWeek]);

  const ratesByDate = useMemo(() => {
    const map = {};
    historicalRates.forEach((rate) => {
      const date = rate.last_update.slice(0, 10);
      if (!map[date]) map[date] = rate.price;
    });
    return map;
  }, [historicalRates]);

  const availableDates = useMemo(
    () => Object.keys(ratesByDate).sort((a, b) => new Date(b) - new Date(a)),
    [ratesByDate]
  );

  const selectedRate =
    form.currency === "BS" ? ratesByDate[rateDate] || "" : currentBcvRate || "";

  useEffect(() => {
    if (editingId) return;
    const start = String(startOfWeek).slice(0, 10);
    const end = String(endOfWeek).slice(0, 10);
    const today = todayKey();
    if (start && end && (today < start || today > end)) {
      setForm((prev) => ({ ...prev, expenseDate: start }));
      if (ratesByDate[start]) setRateDate(start);
    }
  }, [startOfWeek, endOfWeek, editingId, ratesByDate]);

  const weekLabel = useMemo(() => {
    const start = String(startOfWeek).slice(0, 10);
    const end = String(endOfWeek).slice(0, 10);
    const weekNo = end
      ? getISOWeek(new Date(`${end}T12:00:00`))
      : "";
    if (!start || !end) return "Cargando semana…";
    return `Semana ${weekNo} · ${formatWeekDay(start)} — ${formatWeekDay(end)}`;
  }, [startOfWeek, endOfWeek]);

  const resetForm = () => {
    setEditingId("");
    setForm(emptyForm(categories[0]?.name || "Quesera"));
    setRateDate(availableDates[0] || "");
  };

  const openNewForm = () => {
    resetForm();
    setFormOpen(true);
  };

  const closeForm = () => {
    resetForm();
    setFormOpen(false);
  };

  const handleEdit = (row) => {
    setEditingId(row._id);
    const usedBcv = row.conversionDetails?.useBcv !== false && row.currency === "BS";
    const expenseDate = String(row.expenseDate || "").slice(0, 10);
    const savedRateDate = String(row.conversionDetails?.rateDate || expenseDate).slice(0, 10);
    setForm({
      category: categoryLabel(row.category) || categories[0]?.name || "Otros",
      description: row.description || "",
      amount: String(row.amount ?? ""),
      amountUsd:
        row.currency === "BS" && row.conversionDetails?.useBcv === false
          ? String(row.amountInUSD || "")
          : "",
      currency: row.currency || "BS",
      useBcv: usedBcv,
      expenseDate,
      notes: row.notes || "",
    });
    if (usedBcv) {
      setRateDate(ratesByDate[savedRateDate] ? savedRateDate : availableDates[0] || savedRateDate);
    }
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      setFormOpen(true);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.description.trim()) {
      toast.error("Indica una descripción", { position: "top-center" });
      return;
    }
    if (!form.amount) {
      toast.error("Indica el monto", { position: "top-center" });
      return;
    }
    const category = String(form.category || "").trim();
    if (!category) {
      toast.error("Escribe o elige una categoría", { position: "top-center" });
      return;
    }
    const numericAmount = parseFloat(String(form.amount).replace(",", ".")) || 0;
    const numericAmountUsd = parseFloat(String(form.amountUsd || "").replace(",", ".")) || 0;
    if (form.currency === "BS" && form.useBcv && !selectedRate) {
      toast.error("No hay tasa de cambio disponible para la fecha seleccionada.", {
        position: "top-center",
      });
      return;
    }
    if (form.currency === "BS" && !form.useBcv && !(numericAmountUsd > 0)) {
      toast.error("Indica el monto en dólares.", { position: "top-center" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        category,
        amount: String(form.amount).replace(",", "."),
        amountUsd: String(form.amountUsd || "").replace(",", "."),
        useBcv: form.currency === "BS" ? form.useBcv : false,
        exchangeRate:
          form.currency === "BS"
            ? form.useBcv
              ? selectedRate
              : numericAmount / numericAmountUsd
            : undefined,
        rateDate: form.currency === "BS" && form.useBcv ? rateDate : undefined,
      };
      if (editingId) {
        await updateQueseraExpense(editingId, payload);
        toast.success("Gasto actualizado", { position: "top-center" });
      } else {
        await createQueseraExpense(payload);
        toast.success("Gasto registrado", { position: "top-center" });
      }
      closeForm();
      fetchWeek();
      getExpenseCategories()
        .then((res) => setCategories(res.data.data || []))
        .catch(() => {});
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo guardar el gasto", {
        position: "top-center",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`¿Eliminar el gasto "${row.description}"?`)) return;
    try {
      await deleteQueseraExpense(row._id);
      toast.success("Gasto eliminado", { position: "top-center" });
      if (editingId === row._id) closeForm();
      fetchWeek();
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo eliminar", {
        position: "top-center",
      });
    }
  };

  const fieldClass =
    "w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-800 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";

  const numericAmount = parseFloat(String(form.amount).replace(",", ".")) || 0;
  const numericAmountUsd = parseFloat(String(form.amountUsd || "").replace(",", ".")) || 0;
  const rateNum = parseFloat(selectedRate) || 0;
  const manualRate =
    form.currency === "BS" && !form.useBcv && numericAmount > 0 && numericAmountUsd > 0
      ? numericAmount / numericAmountUsd
      : 0;
  const amountInUSD =
    form.currency === "USD"
      ? numericAmount || null
      : form.currency === "BS" && !form.useBcv
        ? numericAmountUsd || null
        : form.currency === "BS" && rateNum > 0 && numericAmount
          ? numericAmount / rateNum
          : null;

  const handleCurrencyChange = (currency) => {
    setForm((prev) => ({
      ...prev,
      currency,
      useBcv: currency === "BS" ? prev.useBcv : true,
      amountUsd: currency === "BS" && !prev.useBcv ? prev.amountUsd : "",
    }));
  };

  const handleExpenseDateChange = (next) => {
    setForm((prev) => ({ ...prev, expenseDate: next }));
    if (form.currency === "BS" && form.useBcv && ratesByDate[next]) {
      setRateDate(next);
    }
  };

  const handleUseBcvChange = (checked) => {
    setForm((prev) => ({
      ...prev,
      useBcv: checked,
      amountUsd: checked ? "" : prev.amountUsd,
    }));
  };

  const renderExpenseFields = () => (
    <>
      <label className="text-xs text-gray-500 sm:col-span-2">
        Categoría
        <input
          className={`${fieldClass} mt-1`}
          list="expense-categories"
          value={form.category}
          placeholder="Escribe una nueva o elige una existente"
          onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
        />
      </label>
      <label className="text-xs text-gray-500 sm:col-span-2">
        Descripción
        <input
          className={`${fieldClass} mt-1`}
          value={form.description}
          placeholder="Ej. Sal, cuajo, gas, limpieza…"
          onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
        />
      </label>
      <label className="text-xs text-gray-500">
        Moneda
        <select
          className={`${fieldClass} mt-1`}
          value={form.currency}
          onChange={(e) => handleCurrencyChange(e.target.value)}
        >
          <option value="BS">BS</option>
          <option value="USD">USD</option>
        </select>
      </label>
      <label className="text-xs text-gray-500">
        Fecha
        <input
          type="date"
          className={`${fieldClass} mt-1`}
          value={form.expenseDate}
          onChange={(e) => handleExpenseDateChange(e.target.value)}
        />
      </label>
      {form.currency === "BS" && (
        <label className="flex items-center gap-2 text-sm text-gray-700 px-1 sm:col-span-2">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            checked={form.useBcv}
            onChange={(e) => handleUseBcvChange(e.target.checked)}
          />
          Usar tasa BCV
        </label>
      )}
      {form.currency === "BS" && form.useBcv && (
        <>
          <label className="text-xs text-gray-500">
            Fecha de la Tasa
            <input
              type="date"
              className={`${fieldClass} mt-1`}
              value={rateDate}
              onChange={(e) => setRateDate(e.target.value)}
              min={availableDates.length > 0 ? availableDates[availableDates.length - 1] : ""}
              max={availableDates.length > 0 ? availableDates[0] : ""}
            />
            {rateDate && !selectedRate && (
              <span className="block text-xs text-amber-600 mt-1">
                No hay tasa BCV registrada para esta fecha.
              </span>
            )}
          </label>
          <label className="text-xs text-gray-500">
            Tasa BCV
            <input
              type="text"
              readOnly
              className={`${fieldClass} mt-1 bg-gray-100`}
              value={selectedRate ? formatRate(selectedRate) : ""}
              placeholder="Sin tasa para esta fecha"
            />
          </label>
        </>
      )}
      <label className="text-xs text-gray-500">
        Monto ({form.currency === "BS" ? "Bs" : "USD"})
        <input
          className={`${fieldClass} mt-1`}
          inputMode="decimal"
          value={form.amount}
          onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
        />
        {form.currency === "BS" && form.useBcv && amountInUSD !== null && numericAmount > 0 && (
          <span className="block text-xs text-gray-500 mt-1">
            Equivalente a: {formatCurrency(amountInUSD, "USD")}
          </span>
        )}
      </label>
      {form.currency === "BS" && !form.useBcv && (
        <>
          <label className="text-xs text-gray-500">
            Monto $
            <input
              className={`${fieldClass} mt-1`}
              inputMode="decimal"
              value={form.amountUsd}
              onChange={(e) => setForm((prev) => ({ ...prev, amountUsd: e.target.value }))}
            />
          </label>
          <label className="text-xs text-gray-500">
            Tasa usada (Bs/$)
            <input
              type="text"
              readOnly
              className={`${fieldClass} mt-1 bg-gray-100`}
              value={manualRate > 0 ? formatRate(manualRate) : ""}
            />
          </label>
        </>
      )}
      <label className="text-xs text-gray-500 sm:col-span-2 lg:col-span-3">
        Nota (opcional)
        <input
          className={`${fieldClass} mt-1`}
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
        />
      </label>
    </>
  );

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Gastos")}</title>
      </Helmet>
      <datalist id="expense-categories">
        {categories.map((item) => (
          <option key={item.slug} value={item.name} />
        ))}
      </datalist>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Gastos"
            subtitle="Quesera, insumos y demás egresos por semana completa (jueves a miércoles)"
          />

          <div className="flex flex-wrap items-center gap-2 mt-4 text-sm text-gray-500">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekDate((d) => subWeeks(d, 1))}
            >
              {"<"}
            </Button>
            <span className="min-w-[220px] font-medium text-gray-700">{weekLabel}</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekDate((d) => addWeeks(d, 1))}
            >
              {">"}
            </Button>
            <Button variant="secondary" size="sm" onClick={() => setWeekDate(new Date())}>
              Hoy
            </Button>
          </div>

          <div className="mt-4 grid grid-cols-1 min-[420px]:grid-cols-2 lg:grid-cols-4 gap-2 max-w-5xl">
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 min-w-0">
              <p className="text-[11px] text-gray-500">Gastos de la semana</p>
              <p className="text-xl font-bold text-gray-900 tabular-nums">{totals.count || 0}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 min-w-0">
              <p className="text-[11px] text-gray-500">Total USD</p>
              <p className="text-base sm:text-lg font-bold text-indigo-700 tabular-nums leading-tight break-words">
                {formatCurrency(totals.amountInUSD)}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 min-w-0">
              <p className="text-[11px] text-gray-500">Total BS</p>
              <p className="text-base sm:text-lg font-bold text-emerald-700 tabular-nums leading-tight break-words">
                {formatCurrency(totals.amountInBS, "BS")}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 min-w-0 min-[420px]:col-span-2 lg:col-span-1">
              <p className="text-[11px] text-gray-500">vs semana pasada</p>
              <p
                className={`text-base sm:text-lg font-bold tabular-nums leading-tight break-words ${
                  (difference.amountInUSD || 0) > 0.009
                    ? "text-red-600"
                    : (difference.amountInUSD || 0) < -0.009
                      ? "text-emerald-700"
                      : "text-gray-700"
                }`}
              >
                {(difference.amountInUSD || 0) > 0 ? "+" : ""}
                {formatCurrency(difference.amountInUSD)}
              </p>
              <p
                className={`text-xs tabular-nums mt-0.5 ${
                  (difference.amountInBS || 0) > 0.009
                    ? "text-red-500"
                    : (difference.amountInBS || 0) < -0.009
                      ? "text-emerald-600"
                      : "text-gray-500"
                }`}
              >
                {(difference.amountInBS || 0) > 0 ? "+" : ""}
                {formatCurrency(difference.amountInBS, "BS")}
                {previousTotals.amountInUSD || previousTotals.amountInBS ? (
                  <span className="text-gray-400">
                    {" "}
                    · anterior {formatCurrency(previousTotals.amountInUSD)}
                  </span>
                ) : (
                  <span className="text-gray-400"> · sin gastos la anterior</span>
                )}
              </p>
            </div>
          </div>

          {byCategory.length > 0 && (
            <div className="hidden md:flex flex-wrap gap-1.5 mt-3">
              <button
                type="button"
                onClick={() => setCategoryFilter("")}
                className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                  !categoryFilter
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-600 border-gray-300 hover:border-indigo-400"
                }`}
              >
                Todas
              </button>
              {byCategory.map((item) => (
                <button
                  type="button"
                  key={item.category}
                  onClick={() => setCategoryFilter(item.category)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${
                    categoryFilter === item.category
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-600 border-gray-300 hover:border-indigo-400"
                  }`}
                >
                  {categoryLabel(item.category)} ({item.count}) · {formatCurrency(item.amountInUSD)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="app-content space-y-4">
          <Card className="p-4 hidden md:block">
            <h3 className="font-semibold text-gray-800 mb-3">
              {editingId ? "Editar gasto" : "Registrar gasto"}
            </h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
              {renderExpenseFields()}
              <div className="flex items-end gap-2 lg:col-span-2">
                <Button
                  type="submit"
                  disabled={
                    saving ||
                    (form.currency === "BS" && form.useBcv && !selectedRate) ||
                    (form.currency === "BS" && !form.useBcv && !form.amountUsd)
                  }
                  className="flex-1"
                >
                  <Plus size={16} />
                  {saving ? "Guardando…" : editingId ? "Guardar" : "Agregar"}
                </Button>
                {editingId && (
                  <Button type="button" variant="secondary" onClick={resetForm}>
                    Cancelar
                  </Button>
                )}
              </div>
            </form>
          </Card>

          <label className="md:hidden text-xs text-gray-500 block">
            Filtrar categoría
            <select
              className={`${fieldClass} mt-1`}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">Todas</option>
              {categories.map((item) => (
                <option key={item.slug} value={item.slug}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>

          {loading ? (
            <LoadingState label="Cargando gastos" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-12">
              No hay gastos registrados en esta semana.
            </p>
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {rows.map((row) => (
                  <Card key={row._id} className="p-4 space-y-2">
                    <div className="flex justify-between gap-2 items-start">
                      <div>
                        <p className="font-semibold text-gray-900">{row.description}</p>
                        <p className="text-[11px] text-gray-400">
                          {categoryLabel(row.category)} · {formatDate(row.expenseDate)}
                        </p>
                      </div>
                      <p className="font-bold text-indigo-700">
                        {formatCurrency(row.amountInUSD)}
                      </p>
                    </div>
                    <p className="text-sm text-gray-600">
                      {formatCurrency(row.amount, row.currency)}
                      {row.notes ? ` · ${row.notes}` : ""}
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => handleEdit(row)}
                        className="text-indigo-500 hover:text-indigo-700 p-1"
                      >
                        <Pencil size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(row)}
                        className="text-red-400 hover:text-red-600 p-1"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </Card>
                ))}
              </div>

              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "date", title: "Fecha" },
                    { key: "category", title: "Categoría" },
                    { key: "desc", title: "Descripción" },
                    { key: "amount", title: "Monto", align: "right" },
                    { key: "usd", title: "USD", align: "right" },
                    { key: "actions", title: "", align: "right" },
                  ]}
                >
                  {rows.map((row) => (
                    <tr key={row._id} className="hover:bg-gray-50">
                      <td className="px-6 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                        {formatDate(row.expenseDate)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-700">
                        {categoryLabel(row.category)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-gray-900">
                        {row.description}
                        {row.notes ? (
                          <span className="block text-xs text-gray-400">{row.notes}</span>
                        ) : null}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right text-gray-700">
                        {formatCurrency(row.amount, row.currency)}
                      </td>
                      <td className="px-6 py-3.5 text-sm text-right font-semibold text-gray-900">
                        {formatCurrency(row.amountInUSD)}
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => handleEdit(row)}
                            className="text-indigo-500 hover:text-indigo-700"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(row)}
                            className="text-red-400 hover:text-red-600"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </AppTable>
              </div>
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={openNewForm}
        className="md:hidden fixed right-4 z-[55] h-14 w-14 rounded-full bg-indigo-600 text-white shadow-lg flex items-center justify-center"
        style={{ bottom: "calc(4.75rem + env(safe-area-inset-bottom, 0px))" }}
        aria-label="Agregar gasto"
      >
        <Plus size={26} />
      </button>

      <AppModalShell
        open={formOpen}
        onClose={closeForm}
        title={editingId ? "Editar gasto" : "Agregar gasto"}
        fitContent
        widthClass="max-w-lg"
        footer={
          <AppModalFooter>
            <Button type="button" variant="secondary" onClick={closeForm} className="w-full sm:w-auto">
              Cancelar
            </Button>
            <Button
              type="submit"
              form="expense-form-mobile"
              disabled={
                saving ||
                (form.currency === "BS" && form.useBcv && !selectedRate) ||
                (form.currency === "BS" && !form.useBcv && !form.amountUsd)
              }
              className="w-full sm:w-auto"
            >
              <Plus size={16} />
              {saving ? "Guardando…" : editingId ? "Guardar" : "Agregar"}
            </Button>
          </AppModalFooter>
        }
      >
        <form id="expense-form-mobile" onSubmit={handleSubmit} className="grid grid-cols-1 gap-3">
          {renderExpenseFields()}
        </form>
      </AppModalShell>
    </>
  );
};

export default ExpensesModule;
